import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { Ref } from 'vue'
import {
  sendMatrixTyping,
  subscribeMatrixTyping,
} from '@/modules/chat-matrix-fast/services/matrixTypingService'

const TYPING_TIMEOUT_MS = 6000
const TYPING_SEND_INTERVAL_MS = 3500

function pluralizePeople(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100

  if (mod100 >= 11 && mod100 <= 14) {
    return 'человек'
  }

  if (mod10 === 1) {
    return 'человек'
  }

  if (mod10 >= 2 && mod10 <= 4) {
    return 'человека'
  }

  return 'человек'
}

/** Форматирует список имён пишущих пользователей (без текущего) в строку для UI. */
export function formatTypingText(userNames: string[]): string {
  if (!userNames.length) {
    return ''
  }

  if (userNames.length === 1) {
    return `${userNames[0]} печатает...`
  }

  if (userNames.length <= 3) {
    return `${userNames.join(', ')} печатают...`
  }

  const rest = userNames.length - 2
  return `${userNames[0]}, ${userNames[1]} и еще ${rest} ${pluralizePeople(rest)} печатают...`
}

export function useMatrixTyping(roomId: Ref<string | null | undefined>) {
  const typingUsers = ref<string[]>([])
  const typingText = computed(() => formatTypingText(typingUsers.value))

  let unsubscribe: (() => void) | null = null
  let stopTimer: number | undefined
  let lastTypingSentAt = 0
  let typingRoomId: string | null = null

  const stopTyping = (targetRoomId = typingRoomId) => {
    window.clearTimeout(stopTimer)
    stopTimer = undefined
    lastTypingSentAt = 0
    typingRoomId = null

    if (targetRoomId) {
      void sendMatrixTyping(targetRoomId, false).catch(() => {})
    }
  }

  const markTyping = () => {
    const currentRoomId = roomId.value
    if (!currentRoomId) {
      return
    }

    const now = Date.now()
    typingRoomId = currentRoomId

    if (now - lastTypingSentAt >= TYPING_SEND_INTERVAL_MS) {
      lastTypingSentAt = now
      void sendMatrixTyping(currentRoomId, true, TYPING_TIMEOUT_MS).catch(() => {})
    }

    window.clearTimeout(stopTimer)
    stopTimer = window.setTimeout(() => {
      stopTyping()
    }, TYPING_TIMEOUT_MS)
  }

  watch(
    roomId,
    currentRoomId => {
      const previousTypingRoomId = typingRoomId
      unsubscribe?.()
      unsubscribe = null
      typingUsers.value = []
      stopTyping(previousTypingRoomId)

      if (currentRoomId) {
        unsubscribe = subscribeMatrixTyping(currentRoomId, userNames => {
          typingUsers.value = userNames
        })
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    unsubscribe?.()
    stopTyping()
  })

  return {
    typingText,
    markTyping,
    stopTyping,
  }
}
